/**
 * Allows administrators to search for users and initiate
 * user impersonation sessions for support, testing, and
 * role-based experience validation.
 */
import { SearchLg, XClose } from "@untitledui/icons";
import React, { useEffect, useMemo, useState } from "react";

import { getMembersByUserType } from "@/features/AdminPortal/services/accessService";
import { startImpersonation } from "@/services/impersonation";

interface SwitchUserModalProps {
  open: boolean;
  onClose: () => void;
}

const SwitchUserModal: React.FC<SwitchUserModalProps> = ({
  open,
  onClose,
}) => {
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] =
    useState<any>(null);

  useEffect(() => {
    if (!open) return;

    loadMembers();
  }, [open]);

  const loadMembers = async () => {
    try {
      setLoading(true);

      const users =
        await getMembersByUserType(2);

      setMembers(users || []);
    } catch (error) {
      console.error(
        "Failed loading members",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = useMemo(() => {
    const term = search
      .trim()
      .toLowerCase();

    const all =
      members?.filter((user) => {
        const text = `
          ${user.displayName ?? ""}
          ${user.mail ?? ""}
        `.toLowerCase();

        return text.includes(term);
      }) ?? [];

    return term
      ? all
      : all.slice(0, 5);
  }, [members, search]);

  const handleViewAsUser = async () => {
    if (!selectedUser?.mail) {
      return;
    }

    try {
      const response =
        await startImpersonation(
          selectedUser.mail.toLowerCase(),
          selectedUser.displayName
        );

      sessionStorage.setItem(
        "impersonationId",
        response.impersonationId
      );

      window.location.reload();
    } catch (error) {
      console.error(
        "Failed to start impersonation",
        error
      );
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1200] flex justify-end bg-black/30"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-[460px] max-w-[95vw] h-full shadow-xl border-l border-[var(--border-color)] bg-[var(--panel-bg)] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <div className="text-md font-semibold">
              Switch User
            </div>

            <div className="text-xs text-gray-500">
              View the application as another
              user.
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close"
          >
            <XClose className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 flex flex-col flex-1 overflow-hidden">
          <div className="relative">
            <SearchLg className="absolute left-3 top-3 h-4 w-4 text-gray-400" />

            <input
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
              placeholder="Search by name or email"
              className="w-full pl-10 pr-4 py-3 rounded-lg border bg-white"
            />
          </div>

          <div className="mt-5 flex-1 overflow-y-auto">
            {loading && (
              <div className="space-y-3">
                {[...Array(5)].map(
                  (_, index) => (
                    <div
                      key={`${index}-skeleton`}
                      className="h-16 rounded-xl bg-gray-200 animate-pulse"
                    />
                  )
                )}
              </div>
            )}

            {!loading &&
              filteredMembers.map((user, index) => {
                const selected =
                  selectedUser?.mail ===
                  user.mail;

                return (
                  <button
                    key={`${user.id || user.mail}-${index}`}
                    onClick={() =>
                      setSelectedUser(user)
                    }
                    className={`w-full text-left p-4 rounded-xl border mb-3 transition-all ${
                      selected
                        ? "border-orange-500 bg-orange-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-semibold">
                        {(
                          user.displayName ??
                          "U"
                        )
                          .split(" ")
                          .map(
                            (x: string) =>
                              x[0]
                          )
                          .join("")
                          .slice(0, 2)}
                      </div>

                      <div>
                        <div className="font-medium text-sm text-gray-900">
                          {user.displayName}
                        </div>

                        <div className="text-xs text-gray-500">
                          {user.mail}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}

            {!loading &&
              filteredMembers.length === 0 && (
                <div className="text-center text-sm text-gray-500 py-10">
                  No users found
                </div>
              )}
          </div>

          <div className="border-t pt-4 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-lg border"
            >
              Cancel
            </button>

            <button
              disabled={!selectedUser}
              onClick={handleViewAsUser}
              className={`px-5 py-2 rounded-lg text-white ${
                selectedUser
                  ? "bg-orange-600 hover:bg-orange-700"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
            >
              View as User
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwitchUserModal;